import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Users, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SearchResponse {
  query: string;
  clients: any[];
  matters: any[];
  total: number;
}

interface SearchBarProps {
  className?: string;
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  className = '',
  placeholder = 'Search clients, matters...'
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const performSearch = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults(null);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get API base URL based on environment
      let baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';
      
      if (typeof window !== 'undefined') {
        if (window.location.hostname.includes('azurestaticapps.net')) {
          baseURL = '/api';
        } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';
        }
      }

      // Call the search API
      const response = await fetch(`${baseURL}/search?q=${encodeURIComponent(searchQuery)}`);
      
      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data: SearchResponse = await response.json();
      setResults(data);
      setIsOpen(true);
    } catch (err) {
      setError('Search failed. Please try again.');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced search
    timeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Handle result click
  const handleResultClick = (result: any, type: 'client' | 'matter') => {
    setIsOpen(false);
    setQuery('');
    
    if (type === 'client') {
      navigate(`/clients/${result.id}`);
    } else if (type === 'matter') {
      navigate(`/matters/${result.id}`);
    }
  };

  // Handle clear search
  const handleClear = () => {
    setQuery('');
    setResults(null);
    setIsOpen(false);
    setError(null);
    inputRef.current?.focus();
  };

  // Handle key navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="block w-full pl-10 pr-10 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
        
        {query && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-400">
              Searching...
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-400">
              {error}
            </div>
          ) : results && results.total > 0 ? (
            <div className="py-2">
              {/* Clients Section */}
              {results.clients.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Clients ({results.clients.length})
                  </div>
                  {results.clients.map((client) => (
                    <button
                      key={`client-${client.id}`}
                      onClick={() => handleResultClick(client, 'client')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-700 flex items-center space-x-3"
                    >
                      <Users className="h-4 w-4 text-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate">{client.name}</div>
                        {client.contactEmail && (
                          <div className="text-gray-400 text-sm truncate">{client.contactEmail}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Matters Section */}
              {results.matters.length > 0 && (
                <div>
                  {results.clients.length > 0 && <div className="border-t border-gray-700 my-2" />}
                  <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Matters ({results.matters.length})
                  </div>
                  {results.matters.map((matter) => (
                    <button
                      key={`matter-${matter.id}`}
                      onClick={() => handleResultClick(matter, 'matter')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-700 flex items-center space-x-3"
                    >
                      <FileText className="h-4 w-4 text-green-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate">{matter.title}</div>
                        <div className="text-gray-400 text-sm truncate">
                          {matter.type} • {matter.status}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : results && results.total === 0 ? (
            <div className="p-4 text-center text-gray-400">
              No results found for "{results.query}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
